import { useMemo, useRef, useState, type ClipboardEvent } from 'react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type CellValueChangedEvent,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { RejectedList } from '../feedback/RejectedList.tsx';
import { useT } from '../layout/i18n.ts';
import {
  applyUpdates,
  parseCell,
  parsePaste,
  totals,
  validateMatrix,
  type MatrixRow,
  type Rejected,
} from './model.ts';

ModuleRegistry.registerModules([AllCommunityModule]);

export interface MatrixGridProps {
  /** Size columns from the style's size grid, in grid order. */
  readonly sizes: readonly string[];
  readonly rows: readonly MatrixRow[];
  readonly onChange: (rows: MatrixRow[]) => void;
  readonly readOnly?: boolean;
  /** Number formatter for totals (from @skm/i18n `formatNumber`). */
  readonly formatNumber?: (n: number) => string;
  readonly label: string;
}

type GridRow = { colourway: string; total: number; pinned?: boolean } & Record<
  string,
  number | string | boolean | null | undefined
>;

/** Editable colourway × size grid (AG Grid Community) with our own TSV paste handler and totals [MB D10]. */
export function MatrixGrid({
  sizes,
  rows,
  onChange,
  readOnly = false,
  formatNumber = String,
  label,
}: MatrixGridProps) {
  const t = useT();
  const apiRef = useRef<GridApi<GridRow> | null>(null);
  const [rejected, setRejected] = useState<readonly Rejected[]>([]);
  const sum = useMemo(() => totals(rows, sizes), [rows, sizes]);
  const blocking = useMemo(() => validateMatrix(rows, sizes), [rows, sizes]);

  const rowData = useMemo<GridRow[]>(
    () =>
      rows.map((r) => ({ colourway: r.colourway, ...r.cells, total: sum.byRow[r.colourway] ?? 0 })),
    [rows, sum],
  );
  const pinnedBottom = useMemo<GridRow[]>(
    () => [{ colourway: t('ui.matrix.total'), ...sum.bySize, total: sum.grand, pinned: true }],
    [sum, t],
  );
  const columnDefs = useMemo<ColDef<GridRow>[]>(
    () => [
      { field: 'colourway', headerName: t('ui.matrix.colourway'), pinned: 'left', editable: false },
      ...sizes.map<ColDef<GridRow>>((s) => ({
        colId: s,
        field: s,
        headerName: s,
        editable: (p) => !readOnly && !p.data?.pinned,
        valueParser: (p) => {
          const r = parseCell(String(p.newValue ?? ''));
          return r.ok ? r.value : p.oldValue;
        },
      })),
      {
        field: 'total',
        headerName: t('ui.matrix.total'),
        editable: false,
        valueFormatter: (p) => formatNumber(Number(p.value ?? 0)),
      },
    ],
    [sizes, readOnly, formatNumber, t],
  );

  const onCellValueChanged = (e: CellValueChangedEvent<GridRow>) => {
    const size = e.colDef.field;
    if (!e.data || !size) return;
    onChange(
      applyUpdates(rows, [{ colourway: e.data.colourway, size, value: Number(e.newValue ?? 0) }]),
    );
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    const focused = apiRef.current?.getFocusedCell();
    const colId = focused?.column.getColId();
    const startCol = Math.max(0, colId ? sizes.indexOf(colId) : 0);
    const result = parsePaste(text, {
      sizes,
      colourways: rows.map((r) => r.colourway),
      startRow: focused?.rowIndex ?? 0,
      startCol,
    });
    setRejected(result.rejected);
    if (result.updates.length) onChange(applyUpdates(rows, result.updates));
  };

  return (
    <div
      className="skm-matrix"
      role="group"
      aria-label={label}
      onPaste={onPaste}
      data-blocking={blocking.length}
    >
      <div style={{ height: 400 }}>
        <AgGridReact<GridRow>
          rowData={rowData}
          pinnedBottomRowData={pinnedBottom}
          columnDefs={columnDefs}
          getRowId={(p) => p.data.colourway}
          enterNavigatesVertically
          enterNavigatesVerticallyAfterEdit
          singleClickEdit
          onGridReady={(e: GridReadyEvent<GridRow>) => {
            apiRef.current = e.api;
          }}
          onCellValueChanged={onCellValueChanged}
        />
      </div>
      <RejectedList items={[...blocking, ...rejected]} />
    </div>
  );
}
